import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const EDGE_INSET = 12;
const GAP = 8;
const MAX_WIDTH = 240;
// Opening one tooltip closes any other (only one open at a time).
const OPEN_EVENT = 'carfido:info-tooltip-open';

// The usable vertical band: the viewport minus fixed bars pinned to its top or bottom
// (the mobile CarFido header and bottom tab bar), so a tooltip never hides under them.
function getVerticalBounds(viewportHeight) {
    let top = EDGE_INSET;
    let bottom = viewportHeight - EDGE_INSET;
    document.querySelectorAll('header, nav').forEach((el) => {
        const position = window.getComputedStyle(el).position;
        if (position !== 'fixed' && position !== 'sticky') return;
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        if (rect.top <= 1 && rect.bottom < viewportHeight / 2) top = Math.max(top, rect.bottom + EDGE_INSET);
        if (rect.bottom >= viewportHeight - 1 && rect.top > viewportHeight / 2) bottom = Math.min(bottom, rect.top - EDGE_INSET);
    });
    return { top, bottom };
}

// Small "i" button with a tooltip, used for helper text across CarFido.
// Mouse: shows on hover. Keyboard: shows on focus. Click/tap toggles it; a tap outside
// or Escape closes it. Screen readers always get the text as the button's description,
// and it is announced when opened by tap.
//
// The tooltip is portalled to <body> (cards with overflow-hidden cannot clip it). It
// opens ABOVE the icon by default — in Settings the controls and warnings sit right
// below the headings that carry an "i" — and flips below only when there is no room
// above. Horizontally it is kept inside the nearest `data-tooltip-boundary` ancestor
// (its card) and the viewport, at most 240px wide, so it never causes sideways scroll.
const InfoTooltip = ({ label, text, placement = 'top' }) => {
    const tooltipId = useId();
    const descriptionId = useId();
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [layout, setLayout] = useState(null);

    const open = hovered || focused || pinned;

    const closeAll = useCallback(() => {
        setHovered(false);
        setFocused(false);
        setPinned(false);
    }, []);

    const updatePosition = useCallback(() => {
        const button = buttonRef.current;
        const tooltip = tooltipRef.current;
        if (!button || !tooltip) return;
        const anchor = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        // Horizontal bounds: the card (if marked), intersected with the viewport.
        let minLeft = EDGE_INSET;
        let maxRight = viewportWidth - EDGE_INSET;
        const boundary = button.closest('[data-tooltip-boundary]');
        if (boundary) {
            const box = boundary.getBoundingClientRect();
            minLeft = Math.max(minLeft, box.left + EDGE_INSET);
            maxRight = Math.min(maxRight, box.right - EDGE_INSET);
        }
        const available = Math.max(120, maxRight - minLeft);
        const maxWidth = Math.min(MAX_WIDTH, available);
        // Apply the width limit before measuring so wrapping is accounted for.
        tooltip.style.maxWidth = `${maxWidth}px`;
        const width = Math.min(tooltip.offsetWidth, maxWidth);
        const height = tooltip.offsetHeight;

        const anchorCenter = anchor.left + anchor.width / 2;
        const left = Math.max(minLeft, Math.min(anchorCenter - width / 2, maxRight - width));

        // Vertical: preferred side if it fits, else the other side, else whichever has
        // more room (clamped into the usable band).
        const bounds = getVerticalBounds(viewportHeight);
        const aboveTop = anchor.top - GAP - height;
        const belowTop = anchor.bottom + GAP;
        const fitsAbove = aboveTop >= bounds.top;
        const fitsBelow = belowTop + height <= bounds.bottom;
        let side;
        if (placement === 'bottom') side = fitsBelow || !fitsAbove ? 'below' : 'above';
        else side = fitsAbove || !fitsBelow ? 'above' : 'below';
        if (!fitsAbove && !fitsBelow) {
            side = anchor.top - bounds.top >= bounds.bottom - anchor.bottom ? 'above' : 'below';
        }
        let top = side === 'above' ? aboveTop : belowTop;
        top = Math.max(bounds.top, Math.min(top, bounds.bottom - height));

        const arrowLeft = Math.max(10, Math.min(anchorCenter - left, width - 10));
        setLayout({ top, left, maxWidth, arrowLeft, placement: side });
    }, [placement]);

    useLayoutEffect(() => {
        if (!open) {
            setLayout(null);
            return;
        }
        updatePosition();
    }, [open, updatePosition]);

    // Only one tooltip open at a time: announce opening, close when another opens.
    useEffect(() => {
        const handleOtherOpened = (event) => {
            if (event.detail !== tooltipId) closeAll();
        };
        window.addEventListener(OPEN_EVENT, handleOtherOpened);
        return () => window.removeEventListener(OPEN_EVENT, handleOtherOpened);
    }, [tooltipId, closeAll]);

    useEffect(() => {
        if (open) window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: tooltipId }));
    }, [open, tooltipId]);

    useEffect(() => {
        if (!open) return undefined;
        const handlePointerDown = (event) => {
            if (buttonRef.current?.contains(event.target) || tooltipRef.current?.contains(event.target)) return;
            closeAll();
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') closeAll();
        };
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, closeAll, updatePosition]);

    // A second tap/click closes it even though the button still has focus.
    const handleClick = () => {
        if (open) closeAll();
        else setPinned(true);
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                aria-label={label}
                aria-expanded={open}
                aria-describedby={descriptionId}
                onClick={handleClick}
                onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
                onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
                onFocus={(e) => {
                    // Keyboard focus only; a tap also focuses the button but is handled by click.
                    let keyboard = true;
                    try { keyboard = e.target.matches(':focus-visible'); } catch { keyboard = true; }
                    if (keyboard) setFocused(true);
                }}
                onBlur={() => setFocused(false)}
                className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#414141] transition before:absolute before:-inset-2 before:content-[''] hover:bg-[#f8f8f8] hover:text-[#1F1F1F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D81F26]/40 aria-expanded:text-[#1F1F1F]"
            >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                </svg>
            </button>
            <span id={descriptionId} className="sr-only">{text}</span>
            <span className="sr-only" aria-live="polite">{pinned ? text : ''}</span>
            {open && createPortal(
                <div
                    ref={tooltipRef}
                    id={tooltipId}
                    role="tooltip"
                    data-placement={layout?.placement}
                    style={{
                        position: 'fixed',
                        top: layout ? layout.top : 0,
                        left: layout ? layout.left : 0,
                        maxWidth: layout ? layout.maxWidth : MAX_WIDTH,
                        visibility: layout ? 'visible' : 'hidden',
                    }}
                    onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
                    onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
                    className="z-[130] w-max rounded-xl bg-[#414141] px-3 py-2 text-xs font-semibold leading-relaxed text-white shadow-[0_12px_30px_rgba(20,20,20,0.25)]"
                >
                    {layout && (
                        <span
                            aria-hidden="true"
                            data-testid="tooltip-arrow"
                            style={{ left: layout.arrowLeft }}
                            className={`absolute h-2.5 w-2.5 -translate-x-1/2 rotate-45 bg-[#414141] ${layout.placement === 'below' ? '-top-1' : '-bottom-1'}`}
                        />
                    )}
                    <span className="relative">{text}</span>
                </div>,
                document.body,
            )}
        </>
    );
};

export default InfoTooltip;
